import Avatar from '@/components/linear/avatar';
import TeamIcon from '@/components/linear/team-icon';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import WorkspaceSwitcher from '@/components/linear/workspace-switcher';
import {
  agentsPath,
  cyclesPath,
  documentsPath,
  draftsPath,
  inboxPath,
  initiativesPath,
  myIssuesPath,
  projectsPath,
  pulsePath,
  reviewsPath,
  sessionPath,
  settingsPath,
  teamPath,
  teamsPath,
  viewsPath,
} from '@/lib/linear-paths';
import { listFavorites, type FavoriteRecord } from '@/lib/favorites';
import { fallbackUser, resolveTeams } from '@/lib/linear-utils';
import { admin_root_path, console_root_path } from '@/rails/routes';
import type { SharedPageProps } from '@/types/shared';
import { Link, router, usePage } from '@inertiajs/react';
import {
  Activity,
  AppWindow,
  BookOpen,
  Bot,
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  GitPullRequest,
  Hexagon,
  Inbox,
  LayoutGrid,
  MoreHorizontal,
  PenSquare,
  Receipt,
  Search,
  Settings,
  SlidersHorizontal,
  Star,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

function pathActive(currentPath: string, url: string): boolean {
  if (url === inboxPath()) {
    return (
      currentPath === '/app' ||
      currentPath === '/app/' ||
      currentPath === inboxPath() ||
      currentPath.startsWith('/app/inbox') ||
      currentPath.startsWith('/app/captures')
    );
  }

  return currentPath === url || currentPath.startsWith(`${url}/`);
}

export default function Sidebar() {
  const {
    currentPath,
    untriagedCount,
    currentCompany,
    teams,
    currentUser,
    favorites: serverFavorites = [],
  } = usePage<SharedPageProps & { favorites?: FavoriteRecord[] }>().props;
  const { setPaletteOpen, setCreateIssueOpen, setShortcutsOpen } = useWorkspaceChrome();
  const resolvedTeams = resolveTeams(teams, currentCompany);
  const user = currentUser ?? fallbackUser();
  const [workspaceOpen, setWorkspaceOpen] = useState(true);
  const [teamsOpen, setTeamsOpen] = useState(true);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(resolvedTeams[0]?.id ?? null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteRecord[]>([]);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const refresh = () => {
      if (serverFavorites.length > 0) {
        setFavorites(serverFavorites);
        return;
      }
      setFavorites(listFavorites());
    };
    refresh();
    window.addEventListener('caprock:favorites', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('caprock:favorites', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [serverFavorites]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointer = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [userMenuOpen]);

  return (
    <aside className="ln-sidebar">
      <div className="ln-sidebar-header">
        <div className="ln-sidebar-top">
          <WorkspaceSwitcher />
          <div className="ln-sidebar-actions">
            <button
              type="button"
              className="ln-icon-btn"
              onClick={() => setPaletteOpen(true)}
              aria-label="Search"
              title="Search"
            >
              <Search size={16} />
            </button>
            <button
              type="button"
              className="ln-icon-btn"
              onClick={() => setCreateIssueOpen(true)}
              aria-label="New issue"
              title="New issue"
            >
              <PenSquare size={16} />
            </button>
          </div>
        </div>
      </div>

      <nav className="ln-nav">
        <Link
          href={pulsePath()}
          className="ln-nav-item"
          data-active={pathActive(currentPath, pulsePath()) ? 'true' : 'false'}
        >
          <Activity />
          <span>Pulse</span>
        </Link>
        <Link
          href={inboxPath()}
          className="ln-nav-item"
          data-active={pathActive(currentPath, inboxPath()) ? 'true' : 'false'}
        >
          <Inbox />
          <span>Inbox</span>
          {untriagedCount > 0 ? <span className="ln-nav-badge">{untriagedCount}</span> : null}
        </Link>
        <Link
          href={myIssuesPath()}
          className="ln-nav-item"
          data-active={pathActive(currentPath, myIssuesPath()) ? 'true' : 'false'}
        >
          <User />
          <span>My issues</span>
        </Link>
        <Link
          href={draftsPath()}
          className="ln-nav-item"
          data-active={pathActive(currentPath, draftsPath()) ? 'true' : 'false'}
        >
          <FileText />
          <span>Drafts</span>
        </Link>
        <Link
          href={reviewsPath()}
          className="ln-nav-item"
          data-active={pathActive(currentPath, reviewsPath()) ? 'true' : 'false'}
        >
          <GitPullRequest />
          <span>Reviews</span>
        </Link>
        <Link
          href={agentsPath()}
          className="ln-nav-item"
          data-active={pathActive(currentPath, agentsPath()) ? 'true' : 'false'}
        >
          <Bot />
          <span>Agents</span>
        </Link>

        <div className="ln-section">
          <button
            type="button"
            className="ln-section-label"
            onClick={() => setWorkspaceOpen((v) => !v)}
          >
            {workspaceOpen ? <ChevronDown /> : <ChevronRight />}
            Workspace
          </button>
          {workspaceOpen ? (
            <>
              <Link
                href={initiativesPath()}
                className="ln-nav-item"
                data-active={pathActive(currentPath, initiativesPath()) ? 'true' : 'false'}
              >
                <Hexagon />
                <span>Initiatives</span>
              </Link>
              <Link
                href={projectsPath()}
                className="ln-nav-item"
                data-active={pathActive(currentPath, projectsPath()) ? 'true' : 'false'}
              >
                <Folder />
                <span>Projects</span>
              </Link>
              <Link
                href="/app/invoices"
                className="ln-nav-item"
                data-active={pathActive(currentPath, '/app/invoices') ? 'true' : 'false'}
              >
                <Receipt />
                <span>Invoices</span>
              </Link>
              <a href={console_root_path()} className="ln-nav-item" data-active="false">
                <AppWindow />
                <span>Platform console</span>
              </a>
              <a href={admin_root_path()} className="ln-nav-item" data-active="false">
                <SlidersHorizontal />
                <span>Platform admin</span>
              </a>
              <Link
                href={viewsPath()}
                className="ln-nav-item"
                data-active={pathActive(currentPath, viewsPath()) ? 'true' : 'false'}
              >
                <LayoutGrid />
                <span>Views</span>
              </Link>
              <Link
                href={documentsPath()}
                className="ln-nav-item"
                data-active={pathActive(currentPath, documentsPath()) ? 'true' : 'false'}
              >
                <BookOpen />
                <span>Documents</span>
              </Link>
              <Link
                href={settingsPath()}
                className="ln-nav-item"
                data-active={pathActive(currentPath, settingsPath()) ? 'true' : 'false'}
              >
                <MoreHorizontal />
                <span>More</span>
              </Link>
            </>
          ) : null}
        </div>

        {favorites.length > 0 ? (
          <div className="ln-section">
            <div className="ln-section-label" style={{ cursor: 'default' }}>
              <Star size={12} />
              Favorites
            </div>
            {favorites.map((favorite) => (
              <Link key={favorite.id} href={favorite.href} className="ln-nav-item">
                {favorite.kind === 'project' ? <Folder /> : <Star />}
                <span>{favorite.identifier ?? favorite.title}</span>
              </Link>
            ))}
          </div>
        ) : null}

        <div className="ln-section">
          <button
            type="button"
            className="ln-section-label"
            onClick={() => setTeamsOpen((v) => !v)}
          >
            {teamsOpen ? <ChevronDown /> : <ChevronRight />}
            Your teams
          </button>
          {teamsOpen
            ? resolvedTeams.map((team) => {
                const expanded = expandedTeamId === team.id;
                return (
                  <div key={team.id}>
                    <button
                      type="button"
                      className="ln-nav-item"
                      data-active={pathActive(currentPath, teamPath(team.id)) ? 'true' : 'false'}
                      onClick={() => setExpandedTeamId(expanded ? null : team.id)}
                    >
                      <TeamIcon color={team.color} />
                      <span style={{ flex: 1 }}>{team.name}</span>
                      {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </button>
                    {expanded ? (
                      <div className="ln-nav-nested">
                        <Link href={`${inboxPath()}?team=${team.id}`} className="ln-nav-item">
                          <Inbox />
                          <span>Triage</span>
                          {team.untriagedCount ? (
                            <span className="ln-nav-badge">{team.untriagedCount}</span>
                          ) : null}
                        </Link>
                        <Link
                          href={teamPath(team.id)}
                          className="ln-nav-item"
                          data-active={
                            pathActive(currentPath, teamPath(team.id)) ? 'true' : 'false'
                          }
                        >
                          <LayoutGrid />
                          <span>Issues</span>
                        </Link>
                        <Link href={`${cyclesPath()}?team=${team.id}`} className="ln-nav-item">
                          <Hexagon />
                          <span>Cycles</span>
                        </Link>
                        <Link href={`${projectsPath()}?team=${team.id}`} className="ln-nav-item">
                          <Folder />
                          <span>Projects</span>
                        </Link>
                        <Link href={`${viewsPath()}?team=${team.id}`} className="ln-nav-item">
                          <LayoutGrid />
                          <span>Views</span>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                );
              })
            : null}
          {resolvedTeams.length === 0 ? (
            <Link href={teamsPath()} className="ln-nav-item">
              <Hexagon />
              <span>Teams</span>
            </Link>
          ) : null}
        </div>
      </nav>

      <div className="ln-sidebar-footer" ref={userMenuRef}>
        <button type="button" className="ln-user-btn" onClick={() => setUserMenuOpen((v) => !v)}>
          <Avatar name={user.name} color={user.avatarColor} size={22} />
          <span
            style={{
              flex: 1,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user.name}
          </span>
        </button>
        {userMenuOpen ? (
          <div className="ln-popover" style={{ bottom: 48, left: 8, right: 8 }}>
            <Link
              href={settingsPath()}
              className="ln-popover-item"
              onClick={() => setUserMenuOpen(false)}
            >
              <Settings size={14} />
              Settings
            </Link>
            <button
              type="button"
              className="ln-popover-item"
              onClick={() => {
                setUserMenuOpen(false);
                setShortcutsOpen(true);
              }}
            >
              Keyboard shortcuts
              <span className="ln-kbd">?</span>
            </button>
            <button
              type="button"
              className="ln-popover-item"
              onClick={() => {
                setUserMenuOpen(false);
                router.delete(sessionPath());
              }}
            >
              Log out
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
